import "./VoiceChatModal.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faTimes,
  faSpinner,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api from "../../../../services/BackendServices";

interface VoiceChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  addChatMessage: any;
  sessionId: string;
  username: string;
  userId: string;
}

export default function VoiceChatModal({
  isOpen,
  onClose,
  addChatMessage,
  sessionId,
  username,
  userId,
}: VoiceChatModalProps) {
  const [listening, setListening] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [botIsSpeaking, setBotIsSpeaking] = useState(false);
  const recognitionRef = useRef<any>(null);
  const sessionIdRef = useRef<string>(sessionId);

  // Mantener siempre el valor actualizado
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const handleStartListening = () => {
    if (recognitionRef.current && !isSpeaking) {
      setListening(true);
      setWaitingForResponse(false);
      recognitionRef.current.start();
    }
  };

  const handleStopListening = () => {
    setListening(false);
    setWaitingForResponse(false);
    speechSynthesis.cancel();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current.abort();
    }
    setIsSpeaking(false);
  };

  const splitTextIntoChunks = (
    text: string,
    maxWordsPerChunk: number = 30
  ): string[] => {
    const words: string[] = text.split(" ");
    const chunks: string[] = [];
    for (let i = 0; i < words.length; i += maxWordsPerChunk) {
      chunks.push(words.slice(i, i + maxWordsPerChunk).join(" "));
    }
    return chunks;
  };

  const speakText = useCallback((text: string) => {
    if ("speechSynthesis" in window) {
      const chunks = splitTextIntoChunks(text);
      let currentChunkIndex = 0;

      const speakNextChunk = () => {
        if (currentChunkIndex < chunks.length) {
          const utterance = new SpeechSynthesisUtterance(
            chunks[currentChunkIndex]
          );
          utterance.lang = "es-ES";
          utterance.onstart = () => {
            setIsSpeaking(true);
            setBotIsSpeaking(true);
          };
          utterance.onend = () => {
            currentChunkIndex++;
            if (currentChunkIndex < chunks.length) {
              speakNextChunk();
            } else {
              setIsSpeaking(false);
              setBotIsSpeaking(false);
              handleStartListening();
            }
          };
          speechSynthesis.speak(utterance);
        }
      };

      speakNextChunk();
    } else {
      toast.error("Tu navegador no soporta la síntesis de voz.");
    }
  }, []);

  const sendToModel = useCallback(
    async (text: string) => {
      const currentSessionId = sessionIdRef.current;

      if (!text.trim() || !currentSessionId) {
        console.warn("❌ Intento de enviar sin sessionId:", currentSessionId);
        toast.error("No se pudo enviar el mensaje: sesión inválida.");
        return;
      }

      addChatMessage(username, text);
      setWaitingForResponse(true);

      try {
        const response = await api.GeneralServices.sendToModel(
          text,
          currentSessionId,
          userId
        );
        const result = response.data;
        addChatMessage("Trixie", result.response);
        speakText(result.response);
      } catch (error: any) {
        console.error(error);
        if (error.name === "AbortError") {
          toast.error("La solicitud fue cancelada.");
        } else if (error.message?.includes("NetworkError")) {
          toast.error("Error de red. Por favor, verifica tu conexión.");
        } else {
          toast.error(error.message || "Error al enviar el mensaje.");
        }
      } finally {
        setWaitingForResponse(false);
      }
    },
    [username, userId, addChatMessage, speakText]
  );

  useEffect(() => {
    if (!("webkitSpeechRecognition" in window)) {
      toast.error("Tu navegador no soporta el reconocimiento de voz.");
      return;
    }

    if (recognitionRef.current) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SpeechRecognition();
    recog.continuous = false;
    recog.interimResults = false;
    recog.lang = "es-ES";

    recog.onresult = (event) => {
      let transcript = event.results[0][0].transcript;
      recog.stop();
      sendToModel(transcript);
    };

    recog.onerror = () => {
      setListening(false);
    };

    recog.onend = () => {
      if (!isSpeaking && !botIsSpeaking) setListening(false);
    };

    recognitionRef.current = recog;
  }, [sendToModel, isSpeaking, botIsSpeaking]);

  const handleStopSpeaking = () => {
    speechSynthesis.cancel(); // Detener la síntesis de voz del bot
    setBotIsSpeaking(false); // Marcar como que el bot ya no está hablando
    setIsSpeaking(false); // Asegurarse de que la síntesis esté detenida
  };

  if (!isOpen) return null;

  return (
    <div className="voice-modal-overlay">
      <div className="voice-modal-content">
        <div className="voice-modal-header">
          <h2>Chat de voz con Trixie</h2>
          <button
            className="voice-close-button"
            onClick={() => {
              onClose();
              handleStopSpeaking();
            }}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="voice-modal-body"
        >
          <p className="voice-instruction">
            {waitingForResponse
              ? "Trixie está pensando..."
              : listening
              ? "Habla ahora..."
              : botIsSpeaking
              ? "Trixie está hablando. Espera a que termine o detén la voz."
              : "Pulsa para hablar."}
          </p>

          <button
            onClick={listening ? handleStopListening : handleStartListening}
            type="button"
            className={`voice-mic-button ${
              listening
                ? "listening"
                : waitingForResponse
                ? "waiting"
                : botIsSpeaking
                ? "bot-speaking"
                : "not-listening"
            }`}
            disabled={botIsSpeaking}
          >
            <FontAwesomeIcon
              icon={waitingForResponse ? faSpinner : faMicrophone}
              size="lg"
              className="icon-mic"
              spin={waitingForResponse}
            />
          </button>

          {botIsSpeaking && (
            <button
              type="button"
              className="voice-stop-speech-button"
              onClick={handleStopSpeaking}
            >
              <FontAwesomeIcon icon={faStop} size="lg" className="icon-stop" />
              Detener respuesta
            </button>
          )}
        </motion.div>
      </div>
    </div>
  );
}
